import React, { useEffect } from 'react';
import Slider from 'react-slick';
import { Shield, Check, Award } from 'lucide-react';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "../styles/SliderStyles.css";

const Certifications = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const certifications = [
    { name: "S.S.Q.:하네스", image: `${process.env.PUBLIC_URL}/images/ssq_box.png`, date: "2015.12", issuer: "SEMES" },
    { name: "S.S.Q.:C-BOX", image: `${process.env.PUBLIC_URL}/images/ssq_box.png`, date: "2015.12", issuer: "SEMES" },
    { name: "ISO 14001", image: `${process.env.PUBLIC_URL}/images/ssq_box.png`, date: "2024.01", issuer: "ICR" },
    { name: "ISO 45001", image: `${process.env.PUBLIC_URL}/images/ssq_box.png`, date: "2024.01", issuer: "ICR" },
  ];

  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    centerMode: true,
    centerPadding: "0px",
    autoplay: true,
    autoplaySpeed: 3000,
    arrows: true,
    prevArrow: <CustomPrevArrow />,
    nextArrow: <CustomNextArrow />,
    dotsClass: "slick-dots custom-dots",
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
          centerMode: false,
        }
      },
      {
        breakpoint: 640,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          centerMode: false,
        }
      }
    ]
  };

  return (
    <div className="bg-white min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[60vh] min-h-[400px] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat bg-fixed"
          style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/images/banner.jpg)` }}
        >
          <div className="absolute inset-0 bg-slate-900/70"></div>
        </div>
        <div className="relative z-10 text-center px-4 animate-slide-up">
          <span className="text-secondary font-bold tracking-widest uppercase mb-4 block">Certifications</span>
          <h1 className="text-5xl md:text-6xl font-display font-bold text-white mb-6">인증 현황</h1>
          <p className="text-xl text-slate-200 max-w-2xl mx-auto font-light">
            엄격한 품질 관리와 기술력으로 검증받은 파워테크의 인증 내역입니다.
          </p>
        </div>
      </section>

      {/* Intro Stats */}
      <section className="py-16 bg-white border-b border-slate-100">
        <div className="section-container">
          <div className="grid md:grid-cols-3 gap-8 text-center text-slate-800">
            <div className="p-4">
              <Shield size={40} className="w-12 h-12 mx-auto text-primary mb-4" />
              <h3 className="font-bold text-lg mb-2">국제 표준 규격</h3>
              <p className="text-slate-500">ISO 인증을 통한 글로벌 품질 기준 준수</p>
            </div>
            <div className="p-4">
              <Check size={40} className="w-12 h-12 mx-auto text-secondary mb-4" />
              <h3 className="font-bold text-lg mb-2">엄격한 품질 관리</h3>
              <p className="text-slate-500">체계적인 QA/QC 시스템 운영</p>
            </div>
            <div className="p-4">
              <Award size={40} className="w-12 h-12 mx-auto text-accent mb-4" />
              <h3 className="font-bold text-lg mb-2">우수 협력사</h3>
              <p className="text-slate-500">주요 대기업 파트너십 및 우수업체 선정</p>
            </div>
          </div>
        </div>
      </section>

      {/* Slider Section */}
      <section className="py-24 bg-slate-50">
        <div className="section-container">
          <div className="text-center mb-16">
            <span className="text-accent font-bold tracking-widest uppercase text-sm">Quality Assurance</span>
            <h2 className="text-4xl font-display font-bold text-slate-900 mt-2">인증서 보유 현황</h2>
          </div>

          <div className="max-w-6xl mx-auto px-8">
            <Slider {...settings} className="certification-slider -mx-4">
              {certifications.map((cert, index) => (
                <div key={index} className="px-4 py-8">
                  <div className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 group">
                    <div className="relative aspect-[3/4] overflow-hidden bg-slate-100">
                      <img
                        src={cert.image}
                        alt={`${cert.name} 인증서`}
                        className="w-full h-full object-contain p-4 transition-transform duration-500 group-hover:scale-105"
                        onError={(e) => (e.target as HTMLImageElement).src = 'https://placehold.co/400x560/f1f5f9/94a3b8?text=Certificate'}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-6">
                        <p className="text-white font-medium">{cert.issuer}</p>
                        <p className="text-white/80 text-sm">{cert.date}</p>
                      </div>
                    </div>
                    <div className="p-6 border-t border-slate-100">
                      <h3 className="text-center font-bold text-xl text-slate-800 group-hover:text-primary transition-colors">{cert.name}</h3>
                    </div>
                  </div>
                </div>
              ))}
            </Slider>
          </div>
        </div>
      </section>
    </div>
  );
};

interface ArrowProps {
  onClick?: () => void;
  style?: React.CSSProperties;
  className?: string;
}

const CustomPrevArrow: React.FC<ArrowProps> = (props) => {
  const { onClick } = props;
  return (
    <button
      onClick={onClick}
      className="absolute left-0 z-10 p-3 text-white bg-slate-800/50 hover:bg-primary hover:scale-110 rounded-full transition-all duration-300 transform -translate-y-1/2 top-1/2 backdrop-blur-sm shadow-lg"
      style={{ left: '-20px' }}
      aria-label="Previous"
    >
      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
      </svg>
    </button>
  );
};

const CustomNextArrow: React.FC<ArrowProps> = (props) => {
  const { onClick } = props;
  return (
    <button
      onClick={onClick}
      className="absolute right-0 z-10 p-3 text-white bg-slate-800/50 hover:bg-primary hover:scale-110 rounded-full transition-all duration-300 transform -translate-y-1/2 top-1/2 backdrop-blur-sm shadow-lg"
      style={{ right: '-20px' }}
      aria-label="Next"
    >
      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
      </svg>
    </button>
  );
};

export default Certifications;
